'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageContainer } from '@/components/layout/PageContainer';
import { api } from '@/lib/api';

export function ForgotPasswordPage() {
  const router = useRouter();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      addToast({ title: 'Алдаа', description: 'Имэйл хаягаа оруулна уу', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const result = await api.auth.forgotPassword(email.trim());
      if (result.token) {
        // Dev mode: token returned for testing
        setResetToken(result.token);
      }
      setSuccess(true);
      addToast({ title: 'Амжилттай', description: 'Хэрэв бүртгэл байвал шинэчлэх холбоос илгээсэн' });
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Шинэчлэх холбоос илгээхэд алдаа гарлаа', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <PageContainer>
        <div className="flex justify-center py-16">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-2xl">Имэйлээ шалгана уу</CardTitle>
              <CardDescription>
                Хэрэв энэ имэйлд бүртгэл байвал бид шинэчлэх холбоос илгээсэн.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Имэйлээ шалгаж, нууц үгээ шинэчлэх зааварчилгааг дагана уу.
              </p>
              {resetToken && process.env.NODE_ENV !== 'production' && (
                <div className="space-y-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs font-semibold text-yellow-800">
                    Хөгжүүлэлтийн горим: Шинэчлэх токен
                  </p>
                  <p className="text-xs text-yellow-700 break-all">
                    {resetToken}
                  </p>
                  <Link href={`/reset-password?token=${resetToken}`}>
                    <Button className="w-full" variant="outline">
                      Нууц үг шинэчлэх хуудас руу очих
                    </Button>
                  </Link>
                </div>
              )}
              <div className="pt-4 border-t">
                <Link href="/login" className="text-sm text-blue-600 hover:underline">
                  Нэвтрэх хуудас руу буцах
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">Нууц үг мартсан</CardTitle>
            <CardDescription>
              Имэйл хаягаа оруулаад бид танд нууц үг шинэчлэх холбоос илгээнэ.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Имэйл
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Илгээж байна...' : 'Шинэчлэх холбоос илгээх'}
              </Button>
              <div className="text-center">
                <Link href="/login" className="text-sm text-blue-600 hover:underline">
                  Нэвтрэх хуудас руу буцах
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
